import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldAlert, MapPin, Info, Crown, Sparkles } from "lucide-react";
import { CATEGORIES } from "@/lib/categories";
import { trackEvent } from "@/lib/ga4";

const SEUIL_SECONDES = 5;

// Habillage luxueux par catégorie (charte FodindinCa enrichie : dégradés + glow).
const THEMES = {
  lago: {
    accent: "#E05638",
    gradient: "linear-gradient(135deg, #E05638 0%, #F2A65A 60%, #D4AF37 100%)",
    glow: "rgba(224,86,56,0.45)",
    card: "#FAF8F5",
    dark: false,
    Icone: Sparkles,
  },
  love: {
    accent: "#0A5C36",
    gradient: "linear-gradient(135deg, #0A5C36 0%, #2E8B57 55%, #D4AF37 100%)",
    glow: "rgba(10,92,54,0.45)",
    card: "#FAF8F5",
    dark: false,
    Icone: Sparkles,
  },
  vip: {
    accent: "#D4AF37",
    gradient: "linear-gradient(135deg, #B8860B 0%, #D4AF37 50%, #F5D77E 100%)",
    glow: "rgba(212,175,55,0.55)",
    card: "#12100E",
    dark: true,
    Icone: Crown,
  },
};

// Pop-up de sécurité anti-arnaque, affiché avant un contact (membres payants).
// props: category (lago|love|vip), onConfirm() exécute l'action, onClose() ferme
export const AlertPopup = ({ category = "lago", onConfirm, onClose }) => {
  const cat = CATEGORIES[category] || CATEGORIES.lago;
  const theme = THEMES[category] || THEMES.lago;
  const { dark, accent, gradient, glow, card, Icone } = theme;
  const [temps, setTemps] = useState(0);
  const [rappel, setRappel] = useState(false);

  useEffect(() => {
    trackEvent("popup_displayed", {
      event_category: "User_Engagement",
      event_label: `Popup_${cat.name}`,
      value: 1,
    });
    const it = setInterval(() => setTemps((t) => (t >= SEUIL_SECONDES ? t : t + 1)), 1000);
    return () => clearInterval(it);
  }, [cat.name]);

  const confirmer = () => {
    if (temps < SEUIL_SECONDES && !rappel) {
      setRappel(true);
      trackEvent("popup_reminder", { event_category: "User_Engagement", event_label: `Popup_Reminder_${cat.name}` });
      return;
    }
    trackEvent("popup_acknowledged", {
      event_category: "User_Engagement",
      event_label: `Popup_Ack_${cat.name}`,
      value: 1,
    });
    onConfirm?.();
  };

  // Nuances de texte selon fond clair / sombre (VIP)
  const cTitle = dark ? "text-white" : "text-stone-900";
  const cBody = dark ? "text-stone-200" : "text-stone-700";
  const cNote = dark ? "text-stone-400" : "text-stone-500";
  const cIconBg = dark ? "rgba(212,175,55,0.16)" : `${accent}1A`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[900] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
      onClick={onClose}
      data-testid="alert-popup-overlay"
    >
      <motion.div
        initial={{ scale: 0.9, y: 24, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        className="relative w-full max-w-md rounded-[28px] overflow-hidden shadow-2xl"
        style={{
          background: card,
          boxShadow: `0 30px 60px -20px rgba(0,0,0,0.55), 0 0 0 1px ${dark ? "rgba(212,175,55,0.25)" : "rgba(0,0,0,0.04)"}`,
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="alert-popup-card"
      >
        {/* halo décoratif */}
        <div
          className="pointer-events-none absolute -top-24 -right-16 w-56 h-56 rounded-full opacity-40 blur-3xl"
          style={{ background: gradient }}
        />
        {/* bandeau dégradé */}
        <div className="h-1.5 w-full" style={{ background: gradient }} />

        <div className="relative p-7">
          <div className="flex items-center gap-3">
            <motion.span
              initial={{ scale: 0.6, rotate: -8 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.08 }}
              className="grid place-items-center w-12 h-12 rounded-2xl shrink-0"
              style={{ background: cIconBg, color: accent, boxShadow: `0 8px 24px -8px ${glow}` }}
            >
              <ShieldAlert size={24} />
            </motion.span>
            <div className="min-w-0">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: accent }}>
                <Icone size={12} /> {cat.name}
              </span>
              <h2 className={`font-heading text-xl font-extrabold ${cTitle}`}>FodindinCa vous rappelle</h2>
            </div>
          </div>

          <div className={`mt-5 space-y-3 text-sm ${cBody}`}>
            <p className="flex gap-2.5">
              <ShieldAlert size={16} className="shrink-0 mt-0.5" style={{ color: accent }} />
              <span><b>Évitez les arnaques :</b> ne payez <b>jamais</b> un transport ou une caution à une personne que vous ne connaissez pas.</span>
            </p>
            <p className="flex gap-2.5">
              <MapPin size={16} className="shrink-0 mt-0.5" style={{ color: accent }} />
              <span><b>Rencontrez en sécurité :</b> privilégiez les lieux publics (cafés, centres commerciaux).</span>
            </p>
            <p className={`flex gap-2.5 text-xs ${cNote}`}>
              <Info size={14} className="shrink-0 mt-0.5" />
              <span><b>Note légale :</b> FodindinCa décline toute responsabilité en cas de pertes ou d'actes malveillants liés à un contact établi via notre plateforme.</span>
            </p>
          </div>

          {rappel && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 rounded-xl px-4 py-3 text-sm font-medium"
              style={{ background: dark ? "rgba(212,175,55,0.12)" : "#FFF8DC", color: dark ? "#F5D77E" : "#B7791F" }}
              data-testid="alert-popup-reminder"
            >
              ⏳ Prenez le temps de lire ces conseils pour votre sécurité !
            </motion.div>
          )}

          <button
            onClick={confirmer}
            className="group mt-6 w-full rounded-full py-3.5 font-heading font-bold text-white relative overflow-hidden transition-transform hover:scale-[1.02] active:scale-95"
            style={{ background: gradient, boxShadow: `0 12px 30px -10px ${glow}` }}
            data-testid="alert-popup-confirm"
          >
            <span
              className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)" }}
            />
            <span className="relative">{rappel ? "Continuer vers le contact" : "J'ai compris"}</span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AlertPopup;
